import React, {
  useCallback, useEffect, useMemo, useReducer, useRef,
} from 'react'
import { useParams } from 'react-router-dom'
import { GroupNavi, HeaderNavi } from '../../components/navigation'
import BaseModal from '../../components/common/BaseModal'
import Loading from '../../components/common/Loading'
import MamoriosMapModal from '../../components/overview/MamoriosMapModal'
import RentalModal from '../../components/overview/RentalModal'
import SearchBar from '../../components/overview/SearchBar'
import GroupMamorioList from '../../components/overview/GroupMamorioList'
import DevicesList from '../../components/overview/DevicesList'
import GpsTrackersList from '../../components/overview/GpsTrackersList'
import QrTagsList from '../../components/overview/QrTagsList'
import Pagination from '../../components/common/Pagination'
import { getMamoriosWithLocation } from '../../helpers/CommonMamorioHelper'
import Unauthorized from '../Unauthorized'
import {
  getGroupDevices,
  getGroupMamorios,
  getGroupMamoriosWithoutUpdatingState,
  SortOrderAsc,
  SortOrderDesc,
} from '../../modules/Mamorio'
import { fetchGroupEventTypes } from '../../modules/QrTagEventTypes'
import { getRootGroupId } from '../../modules/common/session'
import ChannelService from '../../modules/ChannelService'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { closeModal, showModal } from '../../modules/UI'
import { toId } from '../../utils/commonUtils'
import type { Filter } from '../../modules/Mamorio'
import { miniUsersIcon } from '../../icons'
import './Overview.css'
import { ERROR_MODAL } from '../../components/common/RootModal'
import { selectLoadingState } from '../../store'
import { getErrorString } from '../../utils/errorUtils'
import ReloadButton from '../../components/overview/ReloadButton'
import ViewAllMamoriosInMapButton from '../../components/overview/ViewAllMamoriosInMapButton'
import LendMamorioButton from '../../components/overview/LendMamorioButton'
import GroupMamorioCount from '../../components/overview/GroupMamorioCount'

enum OverviewActionType {
  Paginate = 'paginate',
  ChangeGroupId = 'changeGroupId',
  UpdateFilter = 'updateFilter',
  ListType = 'listType',
}

const MamorioListType = 'mamorio'
const DeviceListType = 'device'
const GpsTrackerListType = 'gps_tracker'
const QrTagListType = 'qr_tag'
type ListType = typeof MamorioListType | typeof DeviceListType | typeof GpsTrackerListType | typeof QrTagListType
const LIST_TYPE_STORAGE_PREFIX = 'overview:listType:'

type OverviewState = {
  page?: number
  groupId?: GroupId
  filter?: Filter
  listType: ListType
  qrTagKeyword: string
  qrTagLatestEventType: string
}

type GruopSortDeps = {
  groupId: GroupId | null
  filter: Filter | null
  listType: ListType | null
}

type OverviewAction =
  | {
    type: OverviewActionType.Paginate,
    payload: number
  }
  | {
    type: OverviewActionType.ChangeGroupId,
    payload: GroupId
  }
  | {
    type: OverviewActionType.UpdateFilter
    payload: any
  }
  | {
    type: OverviewActionType.ListType,
    payload: ListType
  }
  | {
    type: 'qrTagKeyword',
    payload: string
  }
  | {
    type: 'qrTagLatestEventType',
    payload: string
  }

const reducer = (state: OverviewState, action: OverviewAction): OverviewState => {
  switch (action.type) {
    case OverviewActionType.Paginate:
      return {
        ...state,
        page: action.payload,
      }
    case OverviewActionType.ChangeGroupId:
      return {
        ...state,
        groupId: action.payload,
      }
    case OverviewActionType.UpdateFilter:
      return {
        ...state,
        filter: action.payload,
      }
    case OverviewActionType.ListType:
      return {
        ...state,
        listType: action.payload,
      }
    case 'qrTagKeyword':
      return {
        ...state,
        qrTagKeyword: action.payload,
      }
    case 'qrTagLatestEventType':
      return {
        ...state,
        qrTagLatestEventType: action.payload,
      }
    default:
      throw new Error()
  }
}

const getListTypeStorageKey = (groupId: GroupId) => `${LIST_TYPE_STORAGE_PREFIX}${groupId}`

const loadStoredListType = (groupId: GroupId): ListType | null => {
  const stored = localStorage.getItem(getListTypeStorageKey(groupId))
  if (stored === MamorioListType || stored === DeviceListType || stored === GpsTrackerListType || stored === QrTagListType) {
    return stored
  }
  return null
}

const Overview = () => {
  const { group_id }: { group_id: string } = useParams()

  const appDispatch = useAppDispatch()
  const team = useAppSelector((state) => state.team)
  const mamorios = useAppSelector((state) => state.mamorio.mamorios)
  const loading = useAppSelector(selectLoadingState)
  const user = useAppSelector((state) => state.user)
  const hasGpsTrackerFeature = Boolean(team.gps_tracker_enabled)
  const hasQrTagFeature = Boolean(team.qr_tag_enabled)
  const hasMamorioCsvDownloadFeature = Boolean(team.mamorio_csv_download_enabled)
  const rentalMode = Boolean(team.groups?.some((group) => group.mode === 'rental'))
  const canUseAdvancedSelector = hasGpsTrackerFeature || hasQrTagFeature
  const availableListTypes = useMemo<ListType[]>(() => {
    const listTypes: ListType[] = [MamorioListType, DeviceListType]

    if (hasGpsTrackerFeature) listTypes.push(GpsTrackerListType)
    if (hasQrTagFeature) listTypes.push(QrTagListType)

    return listTypes
  }, [hasGpsTrackerFeature, hasQrTagFeature])

  const [state, dispatch] = useReducer(reducer, {
    filter: {
      order: SortOrderAsc,
      sort: SortOrderDesc,
    },
    page: 1,
    groupId: group_id ? toId<GroupId>(group_id) : getRootGroupId(),
    listType: MamorioListType,
    qrTagKeyword: '',
    qrTagLatestEventType: '',
  })

  const qrTagEventTypesEntry = useAppSelector((store) => (
    state.groupId ? store.qrTagEventTypes.entries[state.groupId] : undefined
  ))
  const qrTagEventTypeOptions = useMemo(() => {
    const values = (qrTagEventTypesEntry?.values ?? []).map((entry) => entry.name).filter((value) => value.length > 0)
    return Array.from(new Set(values))
  }, [qrTagEventTypesEntry])
  const currentGroup = useMemo(() => (
    team.groups?.find((child) => toId(child.id) === state.groupId)
  ), [state.groupId, team.groups])

  const loadList = useCallback(async (useDeviceApi: boolean) => {
    dispatch({
      type: OverviewActionType.Paginate,
      payload: 1,
    })

    if (useDeviceApi) {
      dispatch({
        type: OverviewActionType.ListType,
        payload: DeviceListType,
      })
    } else {
      dispatch({
        type: OverviewActionType.ListType,
        payload: MamorioListType,
      })
    }
  }, [dispatch])

  const handleListTypeChange = (listType: ListType) => {
    dispatch({
      type: OverviewActionType.ListType,
      payload: listType,
    })
    dispatch({
      type: OverviewActionType.Paginate,
      payload: 1,
    })
  }

  const reloadList = async () => {
    if (!state.groupId || !state.filter) return
    if (state.listType === GpsTrackerListType || state.listType === QrTagListType) {
      return
    }
    try {
      if (state.listType === DeviceListType) {
        await appDispatch(getGroupDevices({
          groupId: state.groupId,
          filter: state.filter,
        })).unwrap()
      } else if (state.listType === MamorioListType) {
        await appDispatch(getGroupMamorios({
          groupId: state.groupId,
          filter: state.filter,
        })).unwrap()
      }
    } catch (e: unknown) {
      appDispatch(closeModal({ modalType: ERROR_MODAL }))
      appDispatch(showModal({
        modalType: ERROR_MODAL,
        modalProps: {
          message: getErrorString(e),
        },
      }))
    }
  }

  const showMamoriosMapModal = () => {
    if (!mamorios) return

    const showMap = (_mamorios: IMamorio[]) => {
      appDispatch(showModal({
        modalType: MamoriosMapModal,
        modalProps: {
          mamorios: getMamoriosWithLocation(_mamorios),
        },
      }))
    }

    if (state.page === 1 && mamorios.length < 29) {
      showMap(mamorios)
      return
    }

    appDispatch(getGroupMamoriosWithoutUpdatingState({
      groupId: state.groupId!,
      filter: {
        page: 1,
        maxMamorios: true,
      },
    })).unwrap().then((result) => {
      showMap(result.mamorios)
    }).catch((error) => {
      console.error(getErrorString(error))
      showMap(mamorios)
    })
  }

  const showRentalModal = (group: IGroup) => {
    appDispatch(showModal({
      modalType: BaseModal,
      modalProps: {
        title: 'このグループのMAMORIOを貸し出す',
        onActionConfirmed: undefined,
        children: <RentalModal group={group} />,
      },
    }))
  }

  const updateGroupFilter = (newFilter: Filter) => {
    dispatch({
      type: OverviewActionType.UpdateFilter,
      payload: { ...state.filter, ...newFilter },
    })
  }

  const onClickHeader = async (filter: Filter, successCallback: () => void) => {
    const newFilter = { ...state.filter, ...filter }

    dispatch({
      type: OverviewActionType.UpdateFilter,
      payload: newFilter,
    })

    successCallback()
  }

  const onChangePage = (pageNumber: number) => {
    dispatch({
      type: OverviewActionType.Paginate,
      payload: pageNumber,
    })
  }

  useEffect(() => {
    dispatch({
      type: OverviewActionType.ChangeGroupId,
      payload: group_id ? toId(group_id) : getRootGroupId(),
    })
  }, [dispatch, group_id])

  useEffect(() => {
    if (typeof window.ChannelIO === 'undefined') {
      ChannelService.bootWithMember(user.user?.id)
    }
  }, [user.user?.id])

  const prevDepsRef = useRef<GruopSortDeps>()
  const prevGroupIdRef = useRef<GroupId | null>(null)

  useEffect(() => {
    if (!state.groupId) return

    const groupChanged = prevGroupIdRef.current !== state.groupId
    prevGroupIdRef.current = state.groupId

    const savedListType = loadStoredListType(state.groupId)
    const isSavedValid = savedListType ? availableListTypes.includes(savedListType) : false

    if (groupChanged && isSavedValid && savedListType && savedListType !== state.listType) {
      dispatch({
        type: OverviewActionType.ListType,
        payload: savedListType,
      })
      dispatch({
        type: OverviewActionType.Paginate,
        payload: 1,
      })
      return
    }

    const fallbackListType = availableListTypes[0]
    if (!availableListTypes.includes(state.listType) && fallbackListType) {
      dispatch({
        type: OverviewActionType.ListType,
        payload: fallbackListType,
      })
      dispatch({
        type: OverviewActionType.Paginate,
        payload: 1,
      })
    }
  }, [availableListTypes, state.groupId, state.listType])

  useEffect(() => {
    if (!state.groupId) return
    if (!availableListTypes.includes(state.listType)) return
    localStorage.setItem(getListTypeStorageKey(state.groupId), state.listType)
  }, [availableListTypes, state.groupId, state.listType])

  useEffect(() => {
    if (!state.groupId) return
    if (state.listType !== QrTagListType) return
    appDispatch(fetchGroupEventTypes({ groupId: state.groupId }))
  }, [appDispatch, state.groupId, state.listType])

  useEffect(() => {
    if (!state.groupId || !state.filter) return

    const newDeps = {
      groupId: state.groupId,
      filter: state.filter,
      listType: state.listType,
    }

    const dependenciesChanged = JSON.stringify(prevDepsRef.current) !== JSON.stringify(newDeps)
    const currentPage = dependenciesChanged ? 1 : state.page
    if (state.listType === GpsTrackerListType || state.listType === QrTagListType) {
      // GPSトラッカー・QRタグの場合は何もしない（各コンポーネント内で独自にfetchする）
      return
    }

    if (state.listType === DeviceListType) {
      appDispatch(getGroupDevices({
        groupId: state.groupId,
        filter: { ...state.filter, page: state.page },
      }))
    } else if (state.listType === MamorioListType) {
      appDispatch(getGroupMamorios({
        groupId: state.groupId,
        filter: { ...state.filter, page: currentPage },
      }))
      // TODO: ソート時に2回fetchしてしまう
      if (dependenciesChanged) {
        onChangePage(1)
      }
    }
    prevDepsRef.current = newDeps
  }, [appDispatch, state.groupId, state.filter, state.listType, state.page])

  if (!currentGroup) {
    return (
      <div className="content">
        <HeaderNavi />
        <Unauthorized />
      </div>
    )
  }

  return (
    <div
      className="content"
      data-testid="overview"
    >
      <HeaderNavi />
      <Loading show={loading} />
      <GroupNavi>
        <>
          <div className="overview-title">
            <div className="overview-title__heading">
              <div className="group-name" data-testid="current-group-name">
                <img src={miniUsersIcon} alt="group-icon" />
                {currentGroup.name}
              </div>
              <GroupMamorioCount mamoriosCount={currentGroup.mamorios_count} />
            </div>
            <div>
              <ReloadButton onClick={reloadList} />
              { mamorios
                && getMamoriosWithLocation(mamorios!)?.length > 0
                && <ViewAllMamoriosInMapButton onClick={showMamoriosMapModal} />}
              {currentGroup.mode === 'rental'
                && <LendMamorioButton onClick={() => showRentalModal(currentGroup)} />}
            </div>
          </div>
          <hr />
          <SearchBar
            rentalMode={rentalMode}
            loadList={loadList}
            updateGroupFilter={updateGroupFilter}
            showSelector={canUseAdvancedSelector}
            currentListType={state.listType as 'mamorio' | 'device' | 'gps_tracker' | 'qr_tag'}
            listTypes={availableListTypes}
            onListTypeChange={handleListTypeChange}
            memoEnabled={currentGroup.memo_enabled}
            qrTagKeyword={state.qrTagKeyword}
            onQrTagSearch={(keyword) => {
              dispatch({ type: 'qrTagKeyword', payload: keyword })
            }}
            qrTagLatestEventType={state.qrTagLatestEventType}
            onQrTagLatestEventTypeChange={(eventType) => {
              dispatch({ type: 'qrTagLatestEventType', payload: eventType })
            }}
            qrTagEventTypeOptions={qrTagEventTypeOptions}
          />
          {(() => {
            if (state.listType === MamorioListType) {
              return (
                <GroupMamorioList
                  group={currentGroup}
                  filter={state.filter}
                  onClickHeader={onClickHeader}
                  onUpdateMamorio={reloadList}
                  csvDownloadEnabled={hasMamorioCsvDownloadFeature}
                  rentalMode={rentalMode}
                />
              )
            }
            if (state.listType === DeviceListType) {
              return (
                <DevicesList
                  filter={state.filter}
                  group={currentGroup}
                />
              )
            }
            if (state.listType === QrTagListType) {
              return (
                <QrTagsList
                  group={currentGroup}
                  keyword={state.qrTagKeyword}
                  latestEventType={state.qrTagLatestEventType}
                />
              )
            }
            return (
              <GpsTrackersList
                group={currentGroup}
              />
            )
          })()}
        </>
        {state.listType === MamorioListType && mamorios && mamorios.length > 0
          && <Pagination onChangePage={onChangePage} />}
      </GroupNavi>
    </div>
  )
}

export default Overview
